import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuPermissions } from '../../hooks/useMenuPermissions';
import {
  documentSignatureService,
  type SignatureValidationResultDto,
} from '../../services/documentSignatureService';
import { ValidatePageLanding } from './ValidatePageLanding';
import { ValidatePageResult } from './ValidatePageResult';
import { isLikelyIpAddress, isSha256Hash } from './validatePageUtils';

export const ValidatePage: React.FC = () => {
  const { publicId } = useParams<{ publicId?: string }>();
  const [searchParams] = useSearchParams();
  const hashParam = searchParams.get('hash');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { getHomePage } = useMenuPermissions();

  const [result, setResult] = useState<SignatureValidationResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const routeDriven = Boolean(publicId || hashParam);
  const showLanding = !routeDriven && result === null;

  useEffect(() => {
    if (!routeDriven) {
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      setResult(null);
      try {
        let data: SignatureValidationResultDto | null = null;
        if (publicId) {
          data = await documentSignatureService.getValidationByPublicId(publicId);
        } else if (hashParam) {
          data = await documentSignatureService.getValidationByHash(hashParam);
        }

        if (!data) {
          setError('Nenhum registro de assinatura encontrado para esta consulta.');
          return;
        }

        setResult(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao validar documento.');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [publicId, hashParam, routeDriven]);

  const handleConsult = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        return;
      }

      if (isLikelyIpAddress(trimmed)) {
        setError(
          'O valor informado parece um endereço IP da evidência de assinatura. Use o ID público (hexadecimal do QR/selo) ou o SHA-256 do PDF.'
        );
        setResult(null);
        return;
      }

      if (isSha256Hash(trimmed)) {
        navigate(`/validate?hash=${encodeURIComponent(trimmed.toLowerCase())}`);
        return;
      }

      navigate(`/validate/${encodeURIComponent(trimmed)}`);
    },
    [navigate]
  );

  const handleUpload = useCallback(async (file: File, optionalPublicId?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await documentSignatureService.verifyUpload(file, optionalPublicId);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao verificar o documento.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleVerifyAnother = useCallback(() => {
    setResult(null);
    setError('');
    navigate('/validate');
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link
            to={isAuthenticated ? getHomePage() : '/validate'}
            className="flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-slate-950"
          >
            <img src="/know-logo.png" alt="Know InDoc" className="h-7 w-auto" />
            <span className="hidden sm:inline">Know InDoc</span>
          </Link>
          {isAuthenticated ? (
            <Link
              to={getHomePage()}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Início
            </Link>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-800"
            >
              Entrar
            </Link>
          )}
        </div>
      </header>

      <div className="px-4 py-10">
        <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-900">Validador de assinatura digital</h1>
          <p className="mt-1 text-sm text-slate-600">Know InDoc — verificação de autenticidade</p>

          {showLanding && (
            <ValidatePageLanding
              loading={loading}
              error={error}
              onConsult={handleConsult}
              onUpload={handleUpload}
            />
          )}

          {!showLanding && loading && !result && (
            <p className="mt-6 text-sm text-slate-500">Consultando registro…</p>
          )}

          {!showLanding && error && !result && (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={handleVerifyAnother}
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                Verificar outro documento
              </button>
            </div>
          )}

          {result && !loading && (
            <ValidatePageResult result={result} onVerifyAnother={handleVerifyAnother} />
          )}
        </div>
      </div>
    </div>
  );
};
